import { collaborateApi, useGetAiSettingsQuery } from 'api/collaborateApi';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AiSettingsFormData } from 'types/api/collaborate';
import { useAppDispatch } from '../store';
import { getSelectedSiteCollaborateInfo, getSelectedSiteId } from '../store/siteInfoSlice';

export function useRealtimeUserSiteSettings() {
  const dispatch = useAppDispatch();
  const siteId = useSelector(getSelectedSiteId);
  const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo);
  const workspaceId = collaborateInfo?.result?.workspace_id || 0;

  const {
    data: aiSettings,
  } = useGetAiSettingsQuery(
    {
      workspaceId: String(workspaceId),
      siteId: siteId || '',
    },
    { skip: !workspaceId || !siteId }
  );


  const handleSiteSettingsUpdated = useCallback(
    (_: unknown) => {
      // const workspace_id = siteInfo?.workspace_id as number;
      // Update the cache with the new site settings
      dispatch(
        collaborateApi.util.invalidateTags([
          { type: 'AiSettings', id: 'LIST' }
        ])
      );

    },
    [dispatch]
  );

  const settingsChannel = useMemo(() => {
    if (!workspaceId) return null;

    // site-specific channel if we have site settings
    if (aiSettings?.site_id) {
      return `sites.${aiSettings.site_id}.ai-settings`;
    }

    // workspace channel for global settings
    return `workspaces.${workspaceId}`;
  }, [workspaceId, aiSettings?.site_id]);

  const handleAiSettingsUpdated = useCallback(
    (e: { settings: AiSettingsFormData; site_id: string | null; workspace_id: number }) => {
      const isGlobalUpdate = !e.site_id;
      const isSiteUpdate = e.site_id === siteId;
      const isRelevantUpdate = isGlobalUpdate || isSiteUpdate;

      if (!isRelevantUpdate) {
        return;
      }

      dispatch(
        collaborateApi.util.updateQueryData(
          'getAiSettings',
          { workspaceId: String(workspaceId), siteId: siteId || '' },
          (draft) => {
            if (draft?.settings) {
              draft.settings = e.settings;
            }
          }
        )
      );
    },
    [workspaceId, siteId, dispatch]
  );

  // Listen to site settings updates using raw Echo API
  useEffect(() => {
    if (!siteId || !window.Echo) return;

    const channelName = `sites.${siteId}.settings`;
    const privateChannel = window.Echo.private(channelName);

    privateChannel.listen('.SiteSettingUpdated', handleSiteSettingsUpdated);

    // Cleanup function
    return () => {
      privateChannel.stopListening('.SiteSettingUpdated');
      window.Echo.leaveChannel(channelName);
    };
  }, [siteId, handleSiteSettingsUpdated]);

  // Listen to AI settings updates using raw Echo API
  useEffect(() => {
    if (!settingsChannel || !window.Echo) return;

    const privateChannel = window.Echo.private(settingsChannel);

    privateChannel.listen('.AiSettingUpdated', handleAiSettingsUpdated);
    privateChannel.listen('.AiSettingCreated', handleAiSettingsUpdated);

    // Cleanup function
    return () => {
      privateChannel.stopListening('.AiSettingUpdated');
      privateChannel.stopListening('.AiSettingCreated');
      window.Echo.leaveChannel(settingsChannel);
    };
  }, [settingsChannel, handleAiSettingsUpdated]);
}
